import React, { memo, useEffect, useMemo, useState, type FC } from 'react';
import { AccessibilityInfo, Platform, Text as RNText, TextStyle } from 'react-native';

import { useScaling } from '@/hooks';
import { theme } from '@/theme/theme';
import { fontFamilies, resolveFontFamily } from '@/theme/typography';
import { TextProps } from './types';
import { getIosBoldTextAdjustedFontWeightStyle } from './ios-bold-text-font-weight';

const Text: FC<TextProps> = ({
  children,
  variant,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  style,
  transform,
  fontStyle = 'normal',
  ...rest
}) => {
  const { scale } = useScaling();
  const [iosBoldTextEnabled, setIosBoldTextEnabled] = useState(false);

  const baseStyle = useMemo(() => {
    const resolvedVariant = (
      variant
        ? {
            fontFamily: theme.typography[variant].fontFamily,
            fontSize: scale(theme.typography[variant].fontSize),
            fontWeight: theme.typography[variant].fontWeight,
            lineHeight: scale(theme.typography[variant].lineHeight),
            letterSpacing: theme.typography[variant].letterSpacing,
            textTransform: theme.typography[variant].textTransform,
          }
        : undefined
    ) as TextStyle;
    if ((Platform.OS === 'android' || Platform.OS === 'web') && resolvedVariant) {
      delete resolvedVariant.fontWeight;
    }
    return resolvedVariant;
  }, [variant, scale]);

  const typographyOverride = useMemo(() => {
    const overrides: {
      fontFamily?: string;
      fontSize?: TextProps['fontSize'];
      fontWeight?: TextProps['fontWeight'];
      lineHeight?: TextProps['lineHeight'];
      letterSpacing?: TextProps['letterSpacing'];
      fontStyle?: TextProps['fontStyle'];
    } = {};

    const _fontFamily = (() => {
      if (fontFamily) return fontFamily;
      if (variant) return theme.typography[variant].fontFamily;
      return fontFamilies.inter;
    })();
    const _fontWeight = (() => {
      if (fontWeight) return fontWeight;
      if (variant) return theme.typography[variant].fontWeight;
      return '400';
    })();
    const _fontStyle = (() => {
      if (fontStyle) return fontStyle;
      return 'normal';
    })();
    const resolvedFontFamily = resolveFontFamily({
      fontFamily: _fontFamily,
      fontWeight: _fontWeight,
      fontStyle: _fontStyle,
    });

    if (resolvedFontFamily.fontFamily) overrides.fontFamily = resolvedFontFamily.fontFamily;
    if (fontSize) overrides.fontSize = fontSize;
    if (_fontWeight && Platform.OS !== 'android' && Platform.OS !== 'web') {
      overrides.fontWeight = _fontWeight;
    }
    if (lineHeight) overrides.lineHeight = lineHeight;
    if (letterSpacing) overrides.letterSpacing = letterSpacing;
    if (fontStyle) overrides.fontStyle = fontStyle;
    return Object.keys(overrides).length > 0 ? overrides : undefined;
  }, [fontFamily, fontSize, fontWeight, lineHeight, letterSpacing, variant, fontStyle]);

  const textTransform = useMemo(() => {
    return transform ? { textTransform: transform } : undefined;
  }, [transform]);

  const iosBoldTextTypographyStyle = useMemo(
    () =>
      iosBoldTextEnabled
        ? getIosBoldTextAdjustedFontWeightStyle(typographyOverride?.fontWeight)
        : undefined,
    [iosBoldTextEnabled, typographyOverride],
  );

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      setIosBoldTextEnabled(false);
      return;
    }

    const sync = (enabled: boolean) => {
      setIosBoldTextEnabled(enabled);
    };

    let cancelled = false;
    (async () => {
      const enabled = await AccessibilityInfo.isBoldTextEnabled();
      if (!cancelled) sync(enabled);
    })();

    const subscription = AccessibilityInfo.addEventListener('boldTextChanged', sync);
    return () => {
      cancelled = true;
      subscription.remove();
    };
  }, []);

  return (
    <RNText
      allowFontScaling={false}
      {...rest}
      style={[baseStyle, typographyOverride, iosBoldTextTypographyStyle, textTransform, style]}>
      {children}
    </RNText>
  );
};

export default memo(Text);
